import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { firstRelation } from "@/lib/relations";
import * as warehouseApi from "@/lib/api/warehouse";
import { useAuth } from "@/context/AuthContext";
import { LoadingState } from "@/components/base/LoadingState";
import { StatusBadge } from "@/components/base/StatusBadge";
import { variantLabel } from "@/lib/format";
import { fulfilmentWorkspacePath, formatShipTo } from "@/lib/fulfilment-workspace";

interface FulfilmentRow {
  id: string;
  fulfilment_number: string;
  status: string;
  sales_orders?: { order_number: string; shipping_address?: Record<string, unknown> | null } | null;
}

interface FulfilmentItemRow extends CatalogueHistoryFields {
  id: string;
  fulfilment_id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_fulfilled: number;
  products?: { name: string; sku: string | null; barcode: string | null } | null;
  variants?: { sku: string | null; barcode: string | null; option_values: Record<string, string> | null } | null;
}

interface VerificationRow {
  id: string;
  fulfilment_id: string;
  fulfilment_item_id: string;
  qty_verified: number;
}

export default function PackingVerification() {
  const { id: fulfilmentId } = useParams();
  const navigate = useNavigate();
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement>(null);
  const [scan, setScan] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const flash = (ok: boolean, text: string) => {
    setFeedback({ ok, text });
    window.setTimeout(() => setFeedback(null), 3000);
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["packing-verify", org?.id, fulfilmentId],
    enabled: !!org?.id && !!fulfilmentId,
    queryFn: async () => {
      const [fulRes, itemsRes, verRes] = await Promise.all([
        supabase
          .from("fulfilments")
          .select("id, fulfilment_number, status, sales_orders(order_number, shipping_address)")
          .eq("org_id", org!.id)
          .eq("id", fulfilmentId!)
          .maybeSingle(),
        supabase
          .from("fulfilment_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, fulfilment_id, product_id, variant_id, qty_fulfilled, products(name, sku, barcode), variants(sku, barcode, option_values)")
          .eq("org_id", org!.id)
          .eq("fulfilment_id", fulfilmentId!),
        supabase
          .from("packing_verifications")
          .select("id, fulfilment_id, fulfilment_item_id, qty_verified")
          .eq("org_id", org!.id)
          .eq("fulfilment_id", fulfilmentId!),
      ]);
      return {
        fulfilment: fulRes.data
          ? {
              ...fulRes.data,
              sales_orders: firstRelation(fulRes.data.sales_orders),
            } as FulfilmentRow
          : null,
        items: (itemsRes.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as FulfilmentItemRow[],
        verifications: (verRes.data ?? []) as VerificationRow[],
      };
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["packing-verify", org?.id, fulfilmentId] });

  // Build barcode lookup map: barcode/sku → variant_id for scan resolution
  const scanMap = useMemo(() => {
    const map: Record<string, string | null> = {};
    (data?.items ?? []).forEach((item) => {
      if (!hasLiveCatalogueIdentity(item)) return;
      const key = item.variant_id ?? item.product_id;
      const codes = [
        item.variants?.barcode,
        item.variants?.sku,
        item.products?.barcode,
        item.products?.sku,
      ].filter(Boolean) as string[];
      codes.forEach((c) => {
        const k = c.trim().toLowerCase();
        if (map[k] === undefined) map[k] = key;
        else if (map[k] !== key) map[k] = null;
      });
    });
    return map;
  }, [data?.items]);

  // Map variant_id → fulfilment_item
  const itemByVariant = useMemo(() => {
    const map: Record<string, FulfilmentItemRow> = {};
    (data?.items ?? []).forEach((item) => {
      const key = item.variant_id ?? item.product_id;
      map[key] = item;
    });
    return map;
  }, [data?.items]);

  // Map fulfilment_item_id → verified qty
  const verifiedByItem = useMemo(() => {
    const map: Record<string, number> = {};
    (data?.verifications ?? []).forEach((v) => {
      map[v.fulfilment_item_id] = v.qty_verified;
    });
    return map;
  }, [data?.verifications]);

  const totalRequired = (data?.items ?? []).reduce((s, it) => s + it.qty_fulfilled, 0);
  const totalVerified = (data?.items ?? []).reduce((s, it) => s + (verifiedByItem[it.id] ?? 0), 0);
  const allVerified = totalRequired > 0 && totalVerified >= totalRequired;
  const progress = totalRequired > 0 ? Math.round((totalVerified / totalRequired) * 100) : 0;

  const handleScan = async () => {
    const code = scan.trim();
    if (!code) return;
    setScan("");
    scanRef.current?.focus();

    const variantOrProduct = scanMap[code.toLowerCase()];
    if (variantOrProduct === undefined) {
      flash(false, "This item is not part of this fulfilment");
      return;
    }
    if (variantOrProduct === null) {
      flash(false, "This code matches multiple items — scan the item-specific barcode or SKU");
      return;
    }

    const item = itemByVariant[variantOrProduct];
    if (!item) {
      flash(false, "This item is not part of this fulfilment");
      return;
    }

    setBusy(true);
    let result: warehouseApi.PackVerificationResult;
    try {
      result = await warehouseApi.recordPackVerification(org!.id, fulfilmentId!, item.variant_id ?? item.product_id, 1);
    } catch (e) {
      setBusy(false);
      flash(false, (e as Error).message);
      return;
    }
    setBusy(false);

    if (result?.error) {
      const msg = result.message ?? result.error ?? "Scan error";
      if (result.error === "wrong_sku") {
        flash(false, "Wrong SKU: This item is not part of this fulfilment");
      } else if (result.error === "over_pack") {
        flash(false, `Over-pack prevented. Required: ${result.required}, already verified: ${result.already_verified}`);
      } else {
        flash(false, msg);
      }
      return;
    }
    refresh();
    flash(true, `Verified +1 — ${result.remaining} remaining`);
  };

  const handleManualIncrement = async (item: FulfilmentItemRow) => {
    setBusy(true);
    let result: warehouseApi.PackVerificationResult;
    try {
      result = await warehouseApi.recordPackVerification(org!.id, fulfilmentId!, item.variant_id ?? item.product_id, 1);
    } catch (e) {
      setBusy(false);
      flash(false, (e as Error).message);
      return;
    }
    setBusy(false);
    if (result?.error) {
      flash(false, result.message ?? result.error ?? "Error");
      return;
    }
    refresh();
    flash(true, `Verified +1`);
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.fulfilment) {
    return (
      <div>
        <div className="mb-5">
          <button onClick={() => navigate(fulfilmentWorkspacePath("packing"))} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back
          </button>
          <h1 className="text-2xl font-semibold text-slate-900">Packing Verification</h1>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this fulfilment.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const f = data.fulfilment;

  return (
    <div className="max-w-2xl">
      <div className="mb-5">
        <button onClick={() => navigate(fulfilmentWorkspacePath("packing"))} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2 whitespace-nowrap">
          <i className="ri-arrow-left-line" /> Back to Fulfilment
        </button>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
              Packing Verification
              <StatusBadge status={f.status} />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              <span className="font-mono font-medium text-slate-700">{f.fulfilment_number}</span>
              {f.sales_orders?.order_number && <> · Order <span className="font-mono">{f.sales_orders.order_number}</span></>}
              {" · "}Deliver to {formatShipTo(f.sales_orders?.shipping_address)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900">
              {totalVerified}<span className="text-slate-400 text-base font-normal"> / {totalRequired}</span>
            </p>
            <div className="w-40 h-2 bg-slate-100 rounded-full overflow-hidden mt-2 ml-auto">
              <div className={`h-full transition-all rounded-full ${allVerified ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div className={`mb-4 px-3 py-2.5 rounded-lg text-sm flex items-center gap-2 ${feedback.ok ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-red-50 border border-red-100 text-red-700"}`} role="status">
          <i className={feedback.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
          {feedback.text}
        </div>
      )}

      {allVerified && (
        <div className="mb-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
          <i className="ri-check-double-line text-emerald-600 text-xl" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">All items verified!</p>
            <p className="text-xs text-emerald-600 mt-0.5">Packing verification complete. You may now seal and dispatch this parcel.</p>
          </div>
        </div>
      )}

      {/* Scanner input */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <label htmlFor="pack-scan-input" className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Scan barcode or SKU</label>
        <div className="flex items-center gap-2">
          <input
            id="pack-scan-input"
            ref={scanRef}
            autoFocus
            value={scan}
            onChange={(e) => setScan(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleScan(); } }}
            placeholder="Scan barcode, then press Enter"
            className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-base font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          />
          <button
            onClick={handleScan}
            disabled={busy || !scan.trim()}
            className="px-4 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-scan-line" /> Verify
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Scan items as you pack them. Over-packing is prevented automatically.</p>
      </div>

      {/* Item list with verification progress */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Items to verify</h3>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/70 border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Barcode / SKU</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Required</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Verified</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((item) => {
                const verified = verifiedByItem[item.id] ?? 0;
                const remaining = item.qty_fulfilled - verified;
                const done = remaining <= 0;
                const code = item.variants?.barcode ?? item.variants?.sku ?? item.products?.barcode ?? item.products?.sku;
                return (
                  <tr key={item.id} className={`border-b border-slate-50 last:border-0 ${done ? "bg-emerald-50/30" : ""}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">
                        {historicalProductName(item)}
                        {(variantLabel(historicalVariantOptions(item)) || historicalSku(item)) && (
                          <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(item)) || historicalSku(item)}</span>
                        )}
                      </p>
                      {done && <span className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Verified ✓</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-500">{code ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.qty_fulfilled}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${done ? "text-emerald-700" : "text-slate-900"}`}>{verified}</span>
                      {!done && <span className="text-slate-400 text-xs ml-1">(+{remaining} left)</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleManualIncrement(item)}
                        disabled={busy || done}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1 ml-auto"
                      >
                        <i className="ri-add-line" /> Verify
                      </button>
                    </td>
                  </tr>
                );
              })}
              {(data?.items ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">No items in this fulfilment</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-slate-50">
          {(data?.items ?? []).map((item) => {
            const verified = verifiedByItem[item.id] ?? 0;
            const remaining = item.qty_fulfilled - verified;
            const done = remaining <= 0;
            const code = item.variants?.barcode ?? item.variants?.sku ?? item.products?.barcode ?? item.products?.sku;
            return (
              <div key={item.id} className={`px-4 py-4 ${done ? "bg-emerald-50/30" : ""}`}>
                <p className="text-sm font-medium text-slate-800">
                  {historicalProductName(item)}
                  {(variantLabel(historicalVariantOptions(item)) || historicalSku(item)) && (
                    <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(item)) || historicalSku(item)}</span>
                  )}
                </p>
                <p className="text-xs text-slate-400 font-mono mt-0.5">{code ?? "—"}</p>
                <p className="text-xs mt-1">
                  <span className={`font-semibold ${done ? "text-emerald-700" : "text-slate-900"}`}>{verified}</span>
                  <span className="text-slate-400"> / {item.qty_fulfilled} verified</span>
                  {!done && <span className="text-amber-600 ml-1">· {remaining} left</span>}
                </p>
                <button
                  onClick={() => handleManualIncrement(item)}
                  disabled={busy || done}
                  className="mt-3 w-full h-12 flex items-center justify-center gap-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-base font-medium"
                >
                  <i className="ri-add-line text-xl" /> Verify
                </button>
              </div>
            );
          })}
          {(data?.items ?? []).length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">No items in this fulfilment</div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-3">
        <button onClick={() => navigate(fulfilmentWorkspacePath("packing"))} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
          Back to Fulfilment
        </button>
        {allVerified && (
          <button
            onClick={() => navigate(fulfilmentWorkspacePath("packing"))}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-checkbox-circle-line" /> Verification Complete
          </button>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-400 space-y-1">
        <p><i className="ri-information-line mr-1" />Packing verification does not change stock — stock was already updated when the order was picked.</p>
        <p><i className="ri-shield-line mr-1" />Over-packing is blocked server-side. Wrong items are rejected immediately. State persists across refreshes.</p>
      </div>
    </div>
  );
}